
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCompanyRegister } from "@/hooks/useCompanyRegister";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { toast } from "sonner";

export function CompanyRegisterStep() {
  const { providers, preferences, updatePreferences, isLoadingProviders } = useCompanyRegister();
  const { isLoading } = useUserPreferences();
  
  const [selectedProvider, setSelectedProvider] = useState<string>("none");
  const [apiKey, setApiKey] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Set initial selected provider from preferences once data is loaded
  useEffect(() => {
    if (!isLoading && preferences?.defaultProviderId) {
      setSelectedProvider(preferences.defaultProviderId);
    }
  }, [isLoading, preferences]);
  
  // Check if the selected provider requires an API key
  const requiresApiKey = React.useMemo(() => {
    if (!selectedProvider || selectedProvider === "none") return false;
    const provider = providers.find(p => p.id === selectedProvider);
    return provider?.requiresApiKey || false;
  }, [selectedProvider, providers]);
  
  const handleSaveProvider = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const updates: Record<string, any> = {
        defaultProviderId: selectedProvider === "none" ? null : selectedProvider
      };
      
      // Only save API key if provider requires it
      if (requiresApiKey && apiKey) {
        updates[`${selectedProvider}_api_key`] = apiKey;
      }
      
      const success = await updatePreferences(updates);
      
      if (success) {
        toast.success("Company register settings saved successfully");
      } else {
        toast.error("Failed to save company register settings");
      }
    } catch (error) {
      console.error("Error saving company register settings:", error);
      toast.error("Failed to save company register settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingProviders) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p>
        Company registers help you quickly look up company information when adding contracts.
        Select which official company register you'd like to use.
      </p>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Company Register Provider</CardTitle>
          <CardDescription>
            Choose which national register to use for company lookups
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="provider">Provider</Label>
            <Select
              value={selectedProvider}
              onValueChange={setSelectedProvider}
              disabled={isSaving}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None - I don't need company lookups</SelectItem>
                {providers.map((provider) => (
                  <SelectItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {requiresApiKey && (
            <div className="space-y-2 mt-4">
              <Label htmlFor="apiKey">API Key</Label>
              <Input
                id="apiKey"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={`Enter your API key`}
                type="password"
                disabled={isSaving}
              />
            </div>
          )}
          
          <Button
            className="mt-4"
            onClick={handleSaveProvider}
            disabled={isSaving || (requiresApiKey && !apiKey)}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>Save Provider</>
            )}
          </Button>
        </CardContent>
      </Card>
      
      <p className="text-sm text-muted-foreground">
        Using an official company register makes it easy to add accurate company 
        information to your contracts, including organizational numbers and addresses.
      </p>
    </div>
  );
}
